import { useTimeoutFn } from '@vueuse/core';
import { get, isPlainObject, mapValues, merge } from 'lodash-es';
import {
  computed,
  ComputedRef,
  nextTick,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  shallowRef,
  unref,
  watch,
  watchEffect,
} from 'vue';
import type { FetchParams, PaginationProps, SorterResult } from '@mts/components/Table';
import type { EmitType } from '@mts-fe/types';
import type { Key } from 'ant-design-vue/es/table/interface';
import { buildUUID, cloneDeep, isArray, isBoolean, isFunction } from '@mts-fe/utils';
import { getSimpleDefaultValue } from '@mts/components/Form/src/helper';
import { parseRowKeyValue } from '@mts/components/Table/src/helper';
import { mtsEmitter } from '@mts/utils';
import { FETCH_SETTING, PAGE_SIZE, ROW_KEY } from '../const';
import type { BasicTableProps } from '../types/table';

interface ActionType {
  getPaginationInfo: ComputedRef<boolean | PaginationProps>;
  setPagination: (info: Partial<PaginationProps>) => void;
  setLoading: (loading: boolean) => void;
  getFieldsValue?: () => Recordable;
  clearSelectedRowKeys: () => void;
  tableData: Ref<Recordable[]>;
}

export function useDataSource(
  propsRef: ComputedRef<BasicTableProps>,
  {
    getPaginationInfo,
    setPagination,
    setLoading,
    getFieldsValue,
    clearSelectedRowKeys,
    tableData,
  }: ActionType,
  emit: EmitType,
) {
  const searchState = {
    sortInfo: {} as Recordable,
    filterInfo: {} as Recordable<string[]>,
  };
  const dataSourceRef = ref<Recordable[]>([]);
  const rawDataSourceRef = shallowRef<Recordable>({});
  const searchInfoRef = shallowRef<Recordable>({});

  watchEffect(() => {
    tableData.value = unref(dataSourceRef);
  });

  watch(
    () => unref(propsRef).dataSource,
    () => {
      const { dataSource, api } = unref(propsRef);
      !api && dataSource && (dataSourceRef.value = dataSource);
    },
    {
      immediate: true,
    },
  );

  function handleTableChange(
    pagination: PaginationProps,
    filters: Partial<Recordable<string[]>>,
    sorter: SorterResult,
  ) {
    const { clearSelectOnPageChange, rowSelection, sortFn, filterFn } = unref(propsRef);
    if (clearSelectOnPageChange || rowSelection?.type === 'radio') {
      clearSelectedRowKeys();
    }
    setPagination(pagination);

    const params: Recordable = {};
    if (sorter && isFunction(sortFn)) {
      const sortInfo = sortFn(sorter);
      searchState.sortInfo = sortInfo;
      params.sortInfo = sortInfo;
    }

    if (filters && isFunction(filterFn)) {
      const filterInfo = filterFn(filters);
      searchState.filterInfo = filterInfo;
      params.filterInfo = filterInfo;
    }
    fetch(params);
  }

  function setTableKey(items: any[]) {
    if (!items || !isArray(items)) return;
    items.forEach((item) => {
      if (!item[ROW_KEY]) {
        item[ROW_KEY] = buildUUID();
      }
      if (item.children && item.children.length) {
        setTableKey(item.children);
      }
    });
  }

  const getAutoCreateKey = computed(() => {
    return unref(propsRef).autoCreateKey && !unref(propsRef).rowKey;
  });

  const getRowKey = computed(() => {
    const { rowKey } = unref(propsRef);
    return unref(getAutoCreateKey) ? ROW_KEY : rowKey;
  });

  const getDataSourceRef: Ref<Recordable<any>[]> = ref([]);

  watch(
    () => dataSourceRef.value,
    () => {
      const dataSource = unref(dataSourceRef);
      if (!dataSource || dataSource.length === 0) {
        getDataSourceRef.value = unref(dataSourceRef);
      }
      if (unref(getAutoCreateKey)) {
        const firstItem = dataSource[0];
        const lastItem = dataSource[dataSource.length - 1];

        if (firstItem && lastItem) {
          if (!firstItem[ROW_KEY] || !lastItem[ROW_KEY]) {
            const data = cloneDeep(unref(dataSourceRef));
            data.forEach((item) => {
              if (!item[ROW_KEY]) {
                item[ROW_KEY] = buildUUID();
              }
              if (item.children && item.children.length) {
                setTableKey(item.children);
              }
            });
            dataSourceRef.value = data;
          }
        }
      }
      getDataSourceRef.value = [...unref(dataSourceRef)];
    },
    {
      flush: 'sync',
      deep: true,
      immediate: true,
    },
  );

  function exchangeTableData(oldIndex: number, newIndex: number) {
    const dataSource = unref(dataSourceRef);
    if (oldIndex > newIndex) {
      dataSource.splice(newIndex, 0, dataSource[oldIndex]);
      dataSource.splice(oldIndex + 1, 1);
    } else {
      dataSource.splice(newIndex + 1, 0, dataSource[oldIndex]);
      dataSource.splice(oldIndex, 1);
    }
    return dataSource;
  }

  async function updateTableData(index: number, key: Key, value: any) {
    const record = dataSourceRef.value[index];
    if (record) {
      dataSourceRef.value[index][key] = value;
    }
    return dataSourceRef.value[index];
  }

  function updateTableDataRecord(
    keyValue: Key,
    record: Recordable,
    append?: boolean,
  ): Recordable | undefined {
    const row = findTableDataRecord(keyValue);

    if (!row) return;

    record[ROW_KEY] = row[ROW_KEY];
    for (const field in row) {
      if (Reflect.has(record, field)) row[field] = record[field];
      if (!append && !Reflect.has(record, field)) row[field] = void 0;
    }
    // 补齐缺失的字段值
    for (const field in record) {
      if (!Reflect.has(row, field)) row[field] = record[field];
    }
    return row;
  }

  function deleteTableDataRecord(keyValues: Key | Key[]) {
    if (!keyValues) {
      console.error('deleteTableDataRecord keyValues is', keyValues);
      return;
    }

    if (!dataSourceRef.value || dataSourceRef.value.length == 0) return;
    const delKeyValues = !isArray(keyValues) ? [keyValues] : keyValues;

    function deleteRow(data, keyValue) {
      const row: { index: number; data: [] } = findRow(data, keyValue);
      if (row === null || row.index === -1) {
        return;
      }
      row.data.splice(row.index, 1);

      function findRow(data, keyValue) {
        if (data === null || data === undefined) {
          return null;
        }
        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          if (parseRowKeyValue(unref(getRowKey), row) === keyValue) {
            return { index: i, data };
          }
          if (row.children?.length > 0) {
            const result = findRow(row.children, keyValue);
            if (result != null) {
              return result;
            }
          }
        }
        return null;
      }
    }

    for (const keyValue of delKeyValues) {
      deleteRow(dataSourceRef.value, keyValue);
      deleteRow(unref(propsRef).dataSource, keyValue);
    }
    setPagination({
      total: unref(propsRef).dataSource?.length,
    });
  }

  function insertTableDataRecord(
    record: Recordable | Recordable[],
    index?: number,
    delCount = 0,
  ): Recordable[] | undefined {
    // if (!dataSourceRef.value || dataSourceRef.value.length == 0) return;
    index = index ?? dataSourceRef.value?.length;
    const _record = isPlainObject(record) ? [record as Recordable] : (record as Recordable[]);
    unref(dataSourceRef).splice(index, delCount, ..._record);
    return unref(dataSourceRef);
  }

  function findTableDataRecord(keyValue: Key) {
    if (!dataSourceRef.value || dataSourceRef.value.length === 0) return;
    const { childrenColumnName = 'children' } = unref(propsRef);

    const findRow = (array: any[]) => {
      let ret;
      array.some(function iter(r) {
        if (parseRowKeyValue(unref(getRowKey), r) === keyValue) {
          ret = r;
          return true;
        }
        return r[childrenColumnName] && r[childrenColumnName].some(iter);
      });
      return ret;
    };

    return findRow(dataSourceRef.value);
  }

  async function fetch(opt?: FetchParams) {
    const {
      api,
      tableId,
      createKeyBy,
      searchCacheKey,
      searchInfo,
      defSort,
      fetchSetting,
      beforeFetch,
      afterFetch,
      useSearchForm,
      formConfig,
      pagination,
    } = unref(propsRef);
    if (!api || !isFunction(api)) return;
    try {
      !opt?.hideLoading && setLoading(true);
      const { pageField, sizeField, listField, totalField } = Object.assign(
        {},
        FETCH_SETTING,
        fetchSetting,
      );
      let pageParams: Recordable = {};

      const { current = 1, pageSize = PAGE_SIZE } = unref(getPaginationInfo) as PaginationProps;

      if ((isBoolean(pagination) && !pagination) || isBoolean(getPaginationInfo)) {
        pageParams = {};
      } else {
        pageParams[pageField] = (opt && opt.page) || current;
        pageParams[sizeField] = pageSize;
      }

      const { sortInfo = {}, filterInfo } = searchState;
      const formValues = getSimpleDefaultValue(formConfig?.schemas);
      if (useSearchForm) {
        await nextTick();
        Object.assign(
          formValues,
          mapValues(getFieldsValue?.(), (i) => (typeof i === 'undefined' || i === '' ? null : i)),
        );
      }

      let params: Recordable = merge(
        pageParams,
        searchInfo,
        formValues,
        opt?.searchInfo ?? {},
        defSort,
        sortInfo,
        opt?.sortInfo ?? {},
        { filterInfo: merge(filterInfo, opt?.filterInfo ?? {}) },
      );
      searchCacheKey && sessionStorage.setItem(searchCacheKey, JSON.stringify(params));
      if (beforeFetch && isFunction(beforeFetch)) {
        const res = await beforeFetch(params);
        if (typeof res === 'boolean' && !res) return;
        params = res || params;
      }
      sessionStorage.setItem('table-fetch-latest-params', JSON.stringify(params));
      mtsEmitter.emit('tableFetch', { tableId, ...params });
      searchInfoRef.value = params;
      const res = await api(params);
      rawDataSourceRef.value = res;

      const isArrayResult = isArray(res);

      let resultItems: Recordable[] = isArrayResult ? res : get(res, listField) || [];
      const resultTotal: number = isArrayResult ? res.length : get(res, totalField);

      // 假如数据变少，导致总页数变少并小于当前选中页码，通过getPaginationRef获取到的页码是不正确的，需获取正确的页码再次执行
      if (Number(resultTotal)) {
        const currentTotalPage = Math.ceil(resultTotal / pageSize);
        if (current > currentTotalPage) {
          setPagination({
            current: currentTotalPage,
          });
          return await fetch(opt);
        }
      }

      if (afterFetch && isFunction(afterFetch)) {
        resultItems = (await afterFetch(resultItems, res)) || resultItems;
      }
      if (createKeyBy) {
        for (const record of resultItems) {
          record.key = createKeyBy.map((i) => record[i]?.toString()).join('@');
        }
      }
      dataSourceRef.value = resultItems;
      setPagination({ total: resultTotal || 0 });
      if (opt && opt.page) {
        setPagination({ current: opt.page || 1 });
      }
      emit('fetch-success', unref(resultItems), resultTotal);
      return resultItems;
    } catch (error) {
      emit('fetch-error', error);
      dataSourceRef.value = [];
      setPagination({
        total: 0,
      });
    } finally {
      emit('fetch-done');
      setLoading(false);
    }
  }

  function setTableData<T = Recordable>(values: T[] = []) {
    dataSourceRef.value = values as Recordable[];
  }

  function getDataSource<T = Recordable>() {
    return getDataSourceRef.value as T[];
  }

  function getRawDataSource<T = Recordable>() {
    return rawDataSourceRef.value as T;
  }

  async function reload(opt?: FetchParams) {
    return await fetch(opt);
  }

  function getSearchInfo<T = Recordable>() {
    return searchInfoRef.value as T;
  }

  onMounted(() => {
    useTimeoutFn(() => {
      const { api, immediate } = unref(propsRef);
      if (api && isFunction(api) && immediate) {
        fetch();
      } else {
        watch(
          () => unref(propsRef).immediate && unref(propsRef).api,
          () => setTimeout(fetch, 0),
        );
      }
    }, 16);
  });

  onUnmounted(() => {
    dataSourceRef.value = [];
    sessionStorage.removeItem('table-fetch-latest-params');
  });

  return {
    getDataSourceRef: computed(() => getDataSourceRef.value),
    getDataSource,
    getRawDataSource,
    searchInfoRef,
    getSearchInfo,
    getRowKey,
    setTableData,
    getAutoCreateKey,
    fetch,
    reload,
    exchangeTableData,
    updateTableData,
    updateTableDataRecord,
    deleteTableDataRecord,
    insertTableDataRecord,
    findTableDataRecord,
    handleTableChange,
  };
}
